/**
 * Pure JS mirror of AnimationRuntime.compile's sequencing math, shared by the
 * clip timeline bars, the dock ruler, Studio lanes and buildSequence — one
 * source of truth so what the UI draws is where the runtime plays.
 */
import { arrayToMap } from "@excalidraw/common";
import {
  getElementsOverlappingFrame,
  isFrameLikeElement,
} from "@excalidraw/element";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
} from "@excalidraw/element/types";

import { createSpring } from "./spring";
import {
  getElementAnimationOrder,
  getElementAnimations,
  getElementStartMode,
} from "./types";

import type { ElementAnimation, ElementStartMode } from "./types";

/** Mirrors AnimationRuntime.compile's sequencing: a clip's sub-timeline is
 * positioned at the previous clip's start ("with") or the running chain end
 * ("after"); its own delay shifts it within the sub-timeline. repeat -1
 * renders as a single cycle (same as export). */
export const layoutClips = (clips: ElementAnimation[]) => {
  let chainEnd = 0;
  let prevStart = 0;
  return clips.map((clip, i) => {
    const duration = clip.spring
      ? createSpring(clip.spring).durationMs
      : clip.duration;
    const cycles = clip.repeat === -1 ? 1 : clip.repeat + 1;
    const base = i > 0 && clip.startMode === "with" ? prevStart : chainEnd;
    const start = base + clip.delay;
    const width =
      duration * cycles + (clip.repeatDelayMs ?? 0) * Math.max(0, cycles - 1);
    prevStart = base;
    chainEnd = Math.max(chainEnd, start + width + (clip.endDelayMs ?? 0));
    return { start, width, infinite: clip.repeat === -1 };
  });
};

/** Running chain end of an element's clip list (its total local length). */
export const clipsEndMs = (clips: ElementAnimation[]): number => {
  const bars = layoutClips(clips);
  // endDelay pads the chain but not the bar width — recompute like layoutClips
  return clips.reduce(
    (end, clip, i) =>
      Math.max(end, bars[i].start + bars[i].width + (clip.endDelayMs ?? 0)),
    0,
  );
};

/** Animated elements in sequence order: stable sort by animationOrder
 * (absent = last, scene order breaks ties). */
export const sequenceAnimatedElements = (
  elements: readonly ExcalidrawElement[],
): ExcalidrawElement[] =>
  elements
    .filter((el) => getElementAnimations(el).length > 0)
    .sort((a, b) => getElementAnimationOrder(a) - getElementAnimationOrder(b));

/** Mirror of compile()'s cross-element cursors: where each element's clip
 * chain starts on the global clock, and the sequenced total. First element
 * starts at 0 (its mode is ignored, like clip index 0); "after" elements
 * start at the running chain end, "with" elements alongside the previous
 * element's start. */
export const layoutElements = (
  elements: readonly ExcalidrawElement[],
): {
  order: ExcalidrawElement[];
  starts: Map<string, number>;
  totalMs: number;
} => {
  const order = sequenceAnimatedElements(elements);
  const starts = new Map<string, number>();
  let chainEnd = 0;
  let prevStart = 0;
  for (const el of order) {
    const start = getElementStartMode(el) === "after" ? chainEnd : prevStart;
    starts.set(el.id, start);
    prevStart = start;
    chainEnd = Math.max(chainEnd, start + clipsEndMs(getElementAnimations(el)));
  }
  return { order, starts, totalMs: chainEnd };
};

export type TimelineLane = {
  element: ExcalidrawElement;
  clips: ElementAnimation[];
  /** the element's start offset on the section's local clock */
  startMs: number;
  mode: ElementStartMode;
};

export type TimelineSection = {
  id: string;
  /** null = the "Canvas" section for elements outside every screen */
  frame: ExcalidrawFrameLikeElement | null;
  /** every member element (animated or not) — what scrubbing compiles, same
   * set export/Studio compile for this screen */
  members: ExcalidrawElement[];
  lanes: TimelineLane[];
  totalMs: number;
};

/** The sequence timeline's screen sections: one per screen that has animated
 * members (each on its own 0-based clock, matching per-screen compile in
 * Studio/export), plus a trailing "Canvas" section for animated elements
 * outside every screen. An element overlapping two screens appears in both
 * (consistent with compile-per-frame). */
export const buildTimelineSections = (
  elements: readonly ExcalidrawElement[],
  screens: readonly ExcalidrawFrameLikeElement[],
): TimelineSection[] => {
  const sections: TimelineSection[] = [];
  const elementsMap = arrayToMap(elements);
  const claimed = new Set<string>();
  const toLanes = (members: ExcalidrawElement[]) => {
    const { order, starts, totalMs } = layoutElements(members);
    return {
      totalMs,
      lanes: order.map((el) => ({
        element: el,
        clips: getElementAnimations(el),
        startMs: starts.get(el.id) ?? 0,
        mode: getElementStartMode(el),
      })),
    };
  };
  for (const frame of screens) {
    const members = getElementsOverlappingFrame(
      elements,
      frame,
      elementsMap,
    ).filter((el) => el.id !== frame.id);
    members.forEach((el) => claimed.add(el.id));
    const { lanes, totalMs } = toLanes(members);
    if (lanes.length > 0) {
      sections.push({ id: frame.id, frame, members, lanes, totalMs });
    }
  }
  const rest = elements.filter(
    (el) => !claimed.has(el.id) && !isFrameLikeElement(el),
  );
  // with no screens the canvas section IS the whole scene — scrubbing it must
  // compile everything (path targets etc.), like the dock always did
  const canvasMembers = screens.length === 0 ? [...elements] : rest;
  const { lanes, totalMs } = toLanes(rest);
  if (lanes.length > 0) {
    sections.push({
      id: "canvas",
      frame: null,
      members: canvasMembers,
      lanes,
      totalMs,
    });
  }
  return sections;
};
